import { useEffect, useState } from "react";
import { axiosInstance } from "contexts/axiosApiInstance";
import { ResourceType } from "./entities";
import type { DirectoryEntry } from "./EmailChipInput";
import { createLogger } from "debug/DebugingLogger";

const log = createLogger("useShareScope");

export interface ShareScope {
  /// `company` = a non-personal org: legacy wire spelling, kept so old and new
  /// builds agree across a deploy.
  scope: "open" | "personal" | "company";
  orgName?: string;
  /// The org roster, company scope only — the universe the share dialog
  /// restricts entry to.
  directory?: DirectoryEntry[];
}

interface OrgMemberResponse {
  email: string;
  display_name: string;
}

const loadRoster = async (orgId: string): Promise<DirectoryEntry[]> => {
  const { data: members } = await axiosInstance.get<OrgMemberResponse[]>(
    `/action/organizations/${orgId}/members`,
  );
  return members
    .filter((m) => m.email)
    .map((m) => ({
      email: m.email,
      displayName: m.display_name || undefined,
    }));
};

/// A non-personal org's table or model shares with members only (D19): suggest the
/// roster and keep entry to it. Advisory; on any failure the door decides.
export const useShareScope = (
  entityId: string,
  resourceType: ResourceType,
  rosterOrg?: { id: string; name: string },
): ShareScope => {
  const [scope, setScope] = useState<ShareScope>({ scope: "open" });
  const modelOrgId = rosterOrg?.id;
  const modelOrgName = rosterOrg?.name;

  useEffect(() => {
    if (!modelOrgId) return;
    let cancelled = false;
    void loadRoster(modelOrgId)
      .then((directory) => {
        if (!cancelled)
          setScope({ scope: "company", orgName: modelOrgName, directory });
      })
      .catch((e) =>
        log.error("model roster lookup failed, keeping free entry:", e),
      );
    return () => {
      cancelled = true;
    };
  }, [modelOrgId, modelOrgName]);

  useEffect(() => {
    // Datasets keep free text until they grow a scope read of their own.
    if (resourceType !== ResourceType.Table || !entityId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await axiosInstance.get<{
          scope: "open" | "personal" | "company";
          org_id?: string;
          org_name?: string;
        }>(`/action/tables/${entityId}/share_scope`);
        if (cancelled) return;
        if (data.scope !== "company" || !data.org_id) {
          setScope({ scope: data.scope });
          return;
        }
        const directory = await loadRoster(data.org_id);
        if (cancelled) return;
        setScope({ scope: "company", orgName: data.org_name, directory });
      } catch (e) {
        log.error("share scope lookup failed, keeping free entry:", e);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [entityId, resourceType]);

  return scope;
};
